import { UserX } from "lucide-react";
import { useId } from "react";
import type { RecipientWarning } from "../../../../shared/contracts.js";
import { ROLE_LABELS, roleLabel } from "../../../../shared/domain.js";

/**
 * Recipients an arrival or correction email cannot reach (spec §9): the
 * assigned advisor or technician when inactive or without an email address,
 * and active managers, foremen or dispatchers without one.
 *
 * Shown before verification (from the preview) and again on the email event
 * after arrival. It informs and never blocks: the arrival still succeeds, and
 * the copy says so, so Parts knows to tell those people another way.
 */

export type { RecipientWarning };

// The role words live in `src/shared/domain.ts` now (the account emails
// print them too); re-exported here so every existing import keeps working.
export { ROLE_LABELS, roleLabel };

export function warningReasonLabel(reason: RecipientWarning["reason"]): string {
  switch (reason) {
    case "inactive":
      return "account is inactive";
    case "missing_email":
      return "no email address on file";
  }
}

export interface RecipientWarningsProps {
  warnings: readonly RecipientWarning[];
  /** Heading shown above the list. */
  title?: string;
  /** Closing line that says the arrival still goes through. */
  footnote?: string;
  className?: string;
}

export function RecipientWarnings({
  warnings,
  title = "Recipients who won't get this email",
  footnote = "The arrival still goes through; let these people know another way.",
  className,
}: RecipientWarningsProps) {
  const headingId = useId();
  if (warnings.length === 0) return null;

  return (
    <section
      className={["recipient-warnings", className].filter(Boolean).join(" ")}
      aria-labelledby={headingId}
    >
      <h3 id={headingId} className="recipient-warnings__title">
        <UserX
          className="recipient-warnings__icon"
          aria-hidden="true"
          focusable="false"
        />
        <span>{title}</span>
      </h3>
      <ul className="recipient-warnings__list" aria-labelledby={headingId}>
        {warnings.map((warning) => (
          <li
            key={`${warning.userId}:${warning.reason}`}
            className="recipient-warnings__item"
          >
            <span className="recipient-warnings__name">
              {warning.displayName}
            </span>{" "}
            <span className="recipient-warnings__role">
              ({roleLabel(warning.role)})
            </span>{" "}
            <span className="recipient-warnings__reason">
              — {warningReasonLabel(warning.reason)}
            </span>
          </li>
        ))}
      </ul>
      <p className="recipient-warnings__footnote">{footnote}</p>
    </section>
  );
}
